import React, { useEffect, useState, useMemo } from "react";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import BackButton from "../common/BackButton";
import { useNavigate } from "react-router-dom";
import { UserRound, Download, RefreshCw } from "lucide-react";
import * as XLSX from 'xlsx';
import toast from "react-hot-toast";

const ExpiredSubscriptions = () => {
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [renewingUserId, setRenewingUserId] = useState(null);
  const navigate = useNavigate();

  const packageTypeOrder = { ONE_BOOK: 1, TWO_BOOKS: 2, THREE_BOOKS: 3 };
  const planNameOrder = { ONE_MONTH: 1, THREE_MONTHS: 3, SIX_MONTHS: 6, ONE_YEAR: 12 };

  useEffect(() => {
    const fetchExpiredUsers = async () => {
      setLoading(true);
      try {
        const data = await SubscriptionManager.getAllExpiredSubscribedUsersWithDetails();
        // const filtered = (data || []).filter(
        //   (user) =>
        //     user.status === "EXPIRED" || (user.remainingDays != null && user.remainingDays <= 0)
        // );
        setExpiredUsers(data);
        setDisplayedUsers(data);
      } catch (error) {
        console.error("Failed to fetch expired subscriptions:", error);
        setExpiredUsers([]);
        setDisplayedUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpiredUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setDisplayedUsers(expiredUsers);
      return;
    }

    const lower = searchTerm.trim().toLowerCase();
    const filtered = expiredUsers.filter((u) => {
      return (
        u.userName?.toLowerCase().includes(lower) ||
        u.emailId?.toLowerCase().includes(lower) ||
        u.planName?.toLowerCase().includes(lower)
      );
    });

    setDisplayedUsers(filtered);
  }, [searchTerm, expiredUsers]);

  const parseDateArray = (dateInput) => {
    // Handle ISO string format
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle array format
    if (!Array.isArray(dateInput) || dateInput.length < 6) return null;
    const [y, m, d, h, min, s] = dateInput;
    return new Date(y, m - 1, d, h, min, s);
  };

  const formatDateArray = (dateInput) => {
    const d = parseDateArray(dateInput);
    return d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedUsers = useMemo(() => {
    const sortable = [...displayedUsers];
    const { key, direction } = sortConfig;
    if (!key) return sortable;

    sortable.sort((a, b) => {
      if (key === "planName") {
        const aVal = planNameOrder[a.planName] || 0;
        const bVal = planNameOrder[b.planName] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (key === "packageType") {
        const aVal = packageTypeOrder[a.packageType] || 0;
        const bVal = packageTypeOrder[b.packageType] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      let aVal = a[key];
      let bVal = b[key];

      if (key === "endDate") {
        const aDate = parseDateArray(aVal);
        const bDate = parseDateArray(bVal);
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [displayedUsers, sortConfig]);

  const handleViewUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleManualRenew = async (e, user) => {
    e.stopPropagation(); // prevent row click
    if (window.confirm(`Are you sure you want to manually renew the previous plan for ${user.userName}?`)) {
      setRenewingUserId(user.userId);
      try {
        await SubscriptionManager.manualRenewUser(user.userId);
        toast.success(`Successfully renewed subscription for ${user.userName}!`);
        // Remove user from expired list since they are now active
        setExpiredUsers(prev => prev.filter(u => u.userId !== user.userId));
        setDisplayedUsers(prev => prev.filter(u => u.userId !== user.userId));
      } catch (error) {
        console.error("Manual renew failed:", error);
        toast.error(error.response?.data?.message || "Failed to renew subscription");
      } finally {
        setRenewingUserId(null);
      }
    }
  };
  
  const handleDownloadExcel = () => {
    if (sortedUsers.length === 0) {
      alert("No data to export");
      return;
    }

    // Prepare data for Excel
    const excelData = sortedUsers.map((user, index) => ({
      "Sr. No.": index + 1,
      "User ID": user.userId,
      "Name": user.userName,
      "Email": user.emailId || "N/A",
      "Contact": user.contactNo || "N/A",
      "Plan": user.planName,
      "Package": user.packageType,
      "End Date": formatDateArray(user.endDate),
      "Status": user.status || "EXPIRED"
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },  // Sr. No.
      { wch: 10 }, // User ID
      { wch: 25 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Contact
      { wch: 15 }, // Plan
      { wch: 15 }, // Package
      { wch: 15 }, // End Date
      { wch: 12 }  // Status
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expired Subscriptions");

    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const filename = `Expired_Subscriptions_${dateStr}.xlsx`;

    // Download
    XLSX.writeFile(workbook, filename);
  };
  
  useEffect(() => {
  setSortConfig({ key: "endDate", direction: "desc" });
}, []);


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Expired Subscribers
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage users with expired plans
            </p>
          </div>

          {/* Right side: Search Bar + Download Button */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search by name, email, or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
              />
            </div>

            {/* Download Excel Button */}
            <button
              onClick={handleDownloadExcel}
              disabled={sortedUsers.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50"
              title="Download Excel"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download Excel</span>
              <span className="sm:hidden">Excel</span>
            </button>
          </div>
        </div>

        {/* Elite Table Container */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-gray-200/60 dark:border-gray-700/50 overflow-hidden ring-1 ring-black/5 dark:ring-white/5">
            <div className="overflow-x-auto elite-scrollbar">
              <style>{`
                .elite-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .elite-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225);
                  border-radius: 3px;
                  transition: background-color 0.2s ease;
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105);
                }
                .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(148 163 184);
                }
                .dark .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full">
                {/* Elite Header with Enhanced Contrast */}
                <thead className="bg-gray-100/80 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700/70 backdrop-blur-sm">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">User</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => handleSort("planName")}>
                      Plan {sortConfig.key === "planName" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => handleSort("packageType")}>
                      Package {sortConfig.key === "packageType" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => handleSort("endDate")}>
                      Expired On {sortConfig.key === "endDate" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Status</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                
                {/* Elite Premium Table Body */}
                <tbody className="bg-white dark:bg-gray-800">
                  {sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        {searchTerm ? `No users match "${searchTerm}."` : "No expired users found."}
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user) => (
                      <tr
                        key={user.userId}
                        className="group border-b border-black/[0.04] dark:border-white/[0.04] transition-all duration-200 ease-out hover:bg-black/[0.02] dark:hover:bg-white/[0.04] cursor-pointer"
                        onClick={() => handleViewUserDetails(user.userId)}
                      >
                        {/* User Column - Name + Contact */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-gray-950 dark:text-white transition-colors duration-150">
                              {user.userName}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {user.contactNo ? user.contactNo : "No Contact"}
                            </span>
                          </div>
                        </td>

                        {/* Plan Column */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {user.planName}
                            </span>
                          </div>
                        </td>

                        {/* Package Column */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {user.packageType}
                            </span>
                          </div>
                        </td>

                        {/* End Date Column */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {formatDateArray(user.endDate)}
                            </span>
                          </div>
                        </td>

                        {/* Status Column */}
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center px-2 py-1 rounded text-[10px] font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200/50 dark:border-red-800/30">
                            {user.status || "EXPIRED"}
                          </span>
                        </td>

                        {/* Actions Column */}
                        <td className="px-3 py-2.5 relative">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={(e) => handleManualRenew(e, user)}
                              disabled={renewingUserId === user.userId}
                              className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-lg transition-all duration-200 
                                ${renewingUserId === user.userId 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800' 
                                  : 'bg-green-50 text-green-700 border border-green-200/50 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30 dark:hover:bg-green-900/40 opacity-0 group-hover:opacity-100'
                                }`}
                              title="Renew Plan"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${renewingUserId === user.userId ? 'animate-spin' : ''}`} />
                              <span className="hidden sm:inline">{renewingUserId === user.userId ? 'Renewing...' : 'Renew'}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewUserDetails(user.userId);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                              title="View User Details"
                            >
                              <UserRound className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpiredSubscriptions;

